<template>
  <div class="planning-list">
    <table class="table">

      <!-- TABLE HEADER -->
      <thead>
        <tr class="table__row table__row--head">
          <th class="table__cell">{{ 'base.date' | translate }}</th>
          <th class="table__cell">{{ 'base.animal_group' | translate }}</th>
          <th class="table__cell">{{ 'base.animal_head' | translate }}</th>
          <th class="table__cell">{{ 'base.weight' | translate }}</th>
          <th class="table__cell">{{ 'base.leftovers' | translate }} [{{ 'base.kg' | translate }}]</th>
          <th class="table__cell nowrap">{{ 'base.leftovers_percent' | translate }}</th>
          <th class="table__cell">{{ 'base.leftovers_feeding' | translate }}</th>
          <th class="table__cell">{{ 'base.leftovers_feeding_fact' | translate }}</th>
        </tr>
      </thead>

      <!-- TABLE BODY -->
      <tbody>
        <!-- Dynamic rows based on data received -->
        <tr class="table__row" v-for="(group, index) in leftovers" :key="index">
          <td class="table__cell no-wrap">{{ group.date_leftover }} </td>
          <td class="table__cell no-wrap">
            <div class="leftovers-group">{{ group.group.name }} <div class="">
                <div @click="openLeftoversInfoDialog(group)" class="leftovers-group-info i-util-i"></div>
              </div>
            </div>
          </td>
          <td class="table__cell nowrap">{{ group.animal_count }}</td>
          <td class="table__cell nowrap">{{ group.fact_weight }}</td>
          <td class="table__cell nowrap">
            <input :disabled="isConsultant" type="number" class="input" :value="group.fact_leftover"
              @blur="changeFactLeftOver($event, group)">
          </td>
          <td class="table__cell nowrap">
            <span class="leftovers-badge" :class="getLeftoversColor(leftoversPercentage(group))">
              {{ formatPercentage(leftoversPercentage(group)) }}%
            </span>
          </td>
          <td class="table__cell nowrap">{{ formatWeight(feedWeightPerHead(group)) }}</td>
          <td class="table__cell nowrap">{{ formatWeight(actualWeightPerHead(group)) }}</td>
        </tr>
        <tr class="table__row" v-if="loaded && isListEmpty">
          <td class="table__cell" colspan="8">{{ 'base.no_data_available' | translate }}</td>
        </tr>

        <!-- TOTALS ROW -->
        <tr class="table__row table__row--totals" v-if="!isListEmpty">
          <td class="table__cell no-wrap">
            <strong>{{ 'base.total' | translate }}</strong>
          </td>
          <td class="table__cell no-wrap">
            {{ totals.groupCount }}
          </td>
          <td class="table__cell nowrap">
            <strong>{{ totals.animalCount }}</strong>
          </td>
          <td class="table__cell nowrap">
            <strong>{{ formatWeight(totals.totalWeight) }}</strong>
          </td>
          <td class="table__cell nowrap">
            <strong>{{ formatWeight(totals.totalLeftovers) }}</strong>
          </td>
          <td class="table__cell nowrap">
            <span class="leftovers-badge" :class="getLeftoversColor(totals.totalLeftoversPercent)">
              <strong>{{ formatPercentage(totals.totalLeftoversPercent) }}%</strong>
            </span>
          </td>
          <td class="table__cell nowrap">
            <strong>{{ formatWeight(totals.avgFeedWeightPerHead) }}</strong>
          </td>
          <td class="table__cell nowrap">
            <strong>{{ formatWeight(totals.avgActualWeightPerHead) }}</strong>
          </td>
        </tr>
      </tbody>
    </table>

    <v-dialog max-width="660" v-model="showLeftoversInfoDialog" @close="closeLeftoversInfoDialog">
      <leftovers-info-dialog :data="activeItem" @close="closeLeftoversInfoDialog" />
    </v-dialog>
  </div>
</template>

<script>
import LeftoversInfoDialog from '../dialogs/LeftoversInfoDialog.vue'
import api from '@/api'

export default {
  name: 'LeftoversList',

  props: {
    leftovers: {
      type: Array,
      default: () => []
    },
    loaded: {
      type: Boolean,
      default: false
    }
  },

  components: {
    LeftoversInfoDialog
  },

  data: () => ({
    showLeftoversInfoDialog: false,
    activeItem: null,
    monitoringSettings: null
  }),

  async mounted () {
    if (!this.farmId) {
      console.warn('⚠️ Cannot load ingredients: no farm ID')
      return
    }
    try {
      const response = await api.monitoring.farms(this.farmId)
      this.monitoringSettings = response.data
    } catch (error) {
      console.error('Error loading monitoring settings:', error)
    }
  },

  computed: {
    farmId () {
      const farmId = this.$route.params.farm_id
      return farmId
    },
    isListEmpty () {
      return this.leftovers.length === 0
    },
    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    },
    totals () {
      if (this.leftovers.length === 0) {
        return {
          groupCount: 0,
          animalCount: 0,
          totalWeight: 0,
          totalLeftovers: 0,
          totalLeftoversPercent: 0,
          avgFeedWeightPerHead: 0,
          avgActualWeightPerHead: 0
        }
      }

      const groupCount = this.leftovers.length
      const animalCount = this.leftovers.reduce((sum, group) => sum + Number(group.animal_count), 0)
      const totalWeight = this.leftovers.reduce((sum, group) => sum + Number(group.fact_weight), 0)
      const totalLeftovers = this.leftovers.reduce((sum, group) => sum + Number(group.fact_leftover), 0)

      const totalLeftoversPercent = totalWeight > 0 ? (totalLeftovers * 100) / totalWeight : 0
      const avgFeedWeightPerHead = animalCount > 0 ? totalWeight / animalCount : 0
      const totalConsumedWeight = totalWeight - totalLeftovers
      const avgActualWeightPerHead = animalCount > 0 ? totalConsumedWeight / animalCount : 0

      return {
        groupCount,
        animalCount,
        totalWeight,
        totalLeftovers,
        totalLeftoversPercent,
        avgFeedWeightPerHead,
        avgActualWeightPerHead
      }
    }
  },

  methods: {
    leftoversPercentage (group) {
      const percentage = ((group.fact_leftover * 100) / group.fact_weight)
      return isNaN(percentage) ? 0 : percentage
    },

    feedWeightPerHead (group) {
      if (group.animal_count !== 0) {
        return (group.fact_weight / group.animal_count)
      }
      return 0
    },

    actualWeightPerHead (group) {
      if (group.animal_count !== 0) {
        return ((group.fact_weight - group.fact_leftover) / group.animal_count)
      }
      return 0
    },

    formatPercentage (value) {
      const num = parseFloat(value)
      return num % 1 === 0 ? num.toString() : num.toFixed(2)
    },

    formatWeight (value) {
      const num = parseFloat(value)
      return num % 1 === 0 ? num.toString() : num.toFixed(2)
    },

    getLeftoversColor (percentage) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      if (typeof percentage !== 'number' || percentage < 0) {
        return ''
      }

      if (!this.monitoringSettings || !this.monitoringSettings.leftovers) {
        return ''
      }

      const settings = this.monitoringSettings.leftovers

      if (!settings.enabled) {
        return ''
      }

      if (settings.green !== 0 && percentage <= settings.green) {
        return 'leftovers-green'
      }

      if (settings.red !== 0 && percentage >= settings.red) {
        return 'leftovers-red'
      }

      if (settings.yellow !== 0) {
        return 'leftovers-yellow'
      }

      return ''
    },

    changeFactLeftOver (event, group) {
      let newValue = event.target.value

      if (newValue === '' || newValue == null) {
        newValue = 0
      } else {
        newValue = Number(newValue)
      }

      group.fact_leftover = newValue

      if (group.fact_weight < group.fact_leftover) {
        group.fact_leftover = group.fact_weight
      }
      this.leftoversPercentage(group)
      this.feedWeightPerHead(group)
      this.actualWeightPerHead(group)
      this.updateGroupData(group)
    },

    async updateGroupData (group) {
      const payload = {
        group: {
          id: group.group.id,
        },
        fact_leftover: group.fact_leftover,
        farm: {
          id: group.farm.id,
        },
      }

      const clientId = this.$store.getters.clientId

      if (this.isAdmin || this.isStaff || this.isConsultant) {
        await api.dashboard.clients.updateLeftoversById(clientId, group.id, payload)
      } else {
        await api.leftovers.updateById(group.id, payload)
      }
    },

    openLeftoversInfoDialog (data) {
      this.activeItem = data
      this.showLeftoversInfoDialog = true
    },

    closeLeftoversInfoDialog () {
      this.activeItem = null
      this.showLeftoversInfoDialog = false
    }
  }
}
</script>

<style scoped>
.list-header {
  display: flex;
  justify-content: space-between;
}

.table__cell.no-wrap {
  white-space: nowrap;
}

.list-title {
  font-size: 14px;
  padding-left: 10px;
  margin-bottom: 10px;
}

.input {
  display: inline;
  border-bottom: 1px solid #979797;
  max-width: 80px;
}

.input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.leftovers-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.leftovers-group-info {
  cursor: pointer;
  padding: 2px;
}

.table__row--totals {
  border-top: 2px solid #ddd;
  background-color: #f9f9f9;
}

.table__row--totals .table__cell {
  font-weight: bold;
}

.leftovers-badge {
  display: inline-block;
  padding: 1rem;
  border-radius: 1rem;
  font-weight: 500;
  min-width: 6rem;
  text-align: center;
}

.leftovers-green {
  background-color: #91EA91;
}

.leftovers-yellow {
  background-color: #FBE268;
}

.leftovers-red {
  background-color: #FF9191;
}
</style>